import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { wp } from "../api/WpApi";
import "../../styles/wpStyles.scss";
import data from "./_setup.json";
import PageLoading from "./PageLoading";
import { Helmet } from "react-helmet";
import parse from "html-react-parser";
import Pagination from "./Pagination";
import Breadcrumbs from "../helpers/Breadcrumbs";
import { removeBreadcrumbs } from "../seo/RemoveBreadcrumbs";
import {readTime} from "../helpers/ReadTime";

import DEFAULT_THUMBNAIL from "../../resources/not-found.png";

const TagList = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [postsperpage] = useState(10);
  const { tagName, page } = useParams();
  const [currentpage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [totalpages, setTotalPages] = useState(null);
  const [tagInfo, setTagInfo] = useState(null);

  let navigate = useNavigate();

  const getPosts = React.useCallback(
    (pageNum) => {
      wp.tags()
        .slug(tagName)
        .then((tags) => {
          if (!tags || tags.length === 0) {
            throw new Error("Tag not found");
          }
          const t = tags[0];
          setTagInfo(t);
          return wp.posts().tags(t.id).perPage(postsperpage).page(pageNum);
        })
        .then((postsResp) => {
          setPosts(postsResp);
          setTotalPages(
            postsResp._paging ? postsResp._paging.totalPages : null
          );
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err);
          setIsLoading(false);
        });
    },
    [tagName, postsperpage]
  );

  useEffect(() => {
    setPosts([]);
    setCurrentPage(page ? parseInt(page, 10) : 1);
    setTotalPages(null);
    setIsLoading(true);
    getPosts(page ? parseInt(page, 10) : 1);

  }, [page, getPosts]);

  if (error) {
    return <div className="content">
      <Helmet>
        <meta name="robots" content="noindex"></meta>
      </Helmet>
      Error: {error.message}
    </div>;
  } else if (isLoading) {
    return (
      <div className="container">
        <PageLoading />
      </div>
    );
  } else {
    const breadcrumbsItems = [
          { name: "Home", url: "/" },
          { name: "All Recipes", url: `/${data.url.slug_posts}/` },
          { name: "Tags", url: `/${data.url.slug_posts}/${data.url.slug_tag}/` },
          {
            name: tagInfo && tagInfo.name ? tagInfo.name : tagName,
            url: `/${data.url.slug_posts}/${data.url.slug_tag}/${tagName}/`,
          },
    ];
    const filteredSchema = removeBreadcrumbs(tagInfo.yoast_head_json.schema, process.env.REACT_APP_PUBLIC_URL);
    return (
      <div className="content container">
        <br />
        {/* {console.log(JSON.stringify(tagInfo.yoast_head_json.schema))} */}
        {tagInfo && tagInfo.yoast_head && (
          <Helmet>{parse(tagInfo.yoast_head)}</Helmet>
        )}
        {tagInfo && tagInfo.yoast_head_json.schema && (
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify(filteredSchema)}
            </script>
          </Helmet>
        )}
        <Helmet>
          <title>
            Results for tag: "{tagInfo ? tagInfo.name : tagName}" | Andrew's
            Cookin'
          </title>
          <link
            rel="canonical"
            href={`${process.env.REACT_APP_PUBLIC_URL}/recipes/tag/${tagName}/${
              currentpage > 1 ? `page-${currentpage}` : ""
            }`}
          />
        </Helmet>
        <div className="row">
          <div className="col">
            <button
              className="btn btn-outline-secondary"
              style={({ float: "left" }, { display: "flex" })}
              onClick={() => navigate(-1)}
            >
              Back
            </button>

            
                <h1 className="fw-bold">
                  Results for tag: "{tagInfo ? tagInfo.name : tagName}"
                </h1>
                <small className="fw-light">
                  {tagInfo ? parse(tagInfo.description || "") : null}
                </small>
          
            
            <Breadcrumbs items={breadcrumbsItems} />
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="list-group col">
            {
              posts.map((post) => (
                <Link
                  key={post.id}
                  to={"/" + data.url.slug_posts + "/" + post.slug + "/"}
                  className="shadow-sm list-group-item list-group-item-action transition ease-in-out delay-50 duration-300 grid grid-rows-3"
                >
                  <h2
                    style={{ padding: 20 }}
                    className="row-span-1"
                    dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                  />
                  <div className="row-span-1 grid grid-cols-1 md:grid-cols-4">
                    {post.yoast_head_json && post.yoast_head_json.og_image ? (
                      <div className="post-thumbnail">
                        <img
                          src={post.yoast_head_json.og_image[0].url}
                          alt={post.yoast_head_json.og_image[0].url}
                        />
                      </div>
                    ) : (
                      <img
                        className="post-thumbnail"
                        loading="lazy"
                        src={DEFAULT_THUMBNAIL}
                        alt="Not Found"
                      ></img>
                    )}
                    <p
                      className="col-span-3 text-left"
                      dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
                    />
                  </div>
                  <div className="row-span-1 grid grid-cols-2 md:grid-cols-3">
                    <div className="md:col-start-3">
                      {new Date(post.date).toLocaleDateString(
                        data.format.date.locale,
                        data.format.date.options
                      )}
                    </div>
                    <p className="md:col-start-2">
                      <u>{readTime(post.content.rendered)}</u>
                    </p>
                  </div>
                </Link>
              ))
            }
          </div>
        </div>
        {totalpages > 1 && (
          <div className="row">
            <div className="col d-flex justify-content-center align-items-center my-5">
              <Pagination
                currentPage={currentpage}
                totalPages={totalpages}
                onPageChange={(pageNum) => {
                  setCurrentPage(pageNum);
                  navigate(
                    pageNum === 1
                      ? `/recipes/tag/${tagName}/`
                      : `/recipes/tag/${tagName}/page-${pageNum}`
                  );
                }}
              />
            </div>
          </div>
        )}
        <hr />
      </div>
    );
  }
};

export default TagList;
